import { useRef, useEffect, useState } from "react"
import { formatNumber } from "~/ui-rtk/utils/format"
import { getLightColor } from "~/ui-rtk/utils/chart-utils"
import { calculatePercentageChange } from "~/ui-rtk/utils/number-utils"
import Card from "~/ui-rtk/components/ui/common/Card"
import { P } from "~/ui-rtk/components/ui/typography"
import MarathonDataLogo from "~/ui-rtk/components/ui/svg/Logo"
import type { PlottedRow } from "~/ui-rtk/pages/WebMerchandising/types"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

export interface FunnelStage {
  label: string
  value: number
  percentage?: number
  abandonmentCount?: number
  abandonmentRate?: number
  // Optional revenue metrics
  revenue?: number
  avgOrderValue?: number
  potentialRevenue?: number
  lostRevenue?: number
  // Optional order breakdown
  firstOrders?: number
  repeatOrders?: number
  // Optional LTV
  avgLtv90Day?: number
}

export interface FunnelChartProps {
  stages: FunnelStage[]
  compareStages?: FunnelStage[]
  plottedRows?: PlottedRow[]
  height?: number
  barWidth?: number
  barColor?: string
  flowColor?: string
  textColor?: string
  textSecondaryColor?: string
  gridColor?: string
  backgroundColor?: string
  showStepLabels?: boolean
  showAbandonmentRates?: boolean
  showGridLines?: boolean
  showValuesInBars?: boolean
  onHover?: (stageIndex: number | null) => void
}

const DEFAULT_COLORS = {
  bar: "#EB89B5",
  flow: "#EB89B5",
  text: "#E5E7EB",
  textSecondary: "#9CA3AF",
  grid: "#374151",
  background: "#1F2937",
}

export default function FunnelChart({
  stages,
  compareStages,
  plottedRows = [],
  height = 400,
  barWidth = 200,
  barColor = DEFAULT_COLORS.bar,
  flowColor = DEFAULT_COLORS.flow,
  textColor = DEFAULT_COLORS.text,
  textSecondaryColor = DEFAULT_COLORS.textSecondary,
  gridColor = DEFAULT_COLORS.grid,
  backgroundColor = DEFAULT_COLORS.background,
  showStepLabels = true,
  showAbandonmentRates = true,
  showGridLines = true,
  showValuesInBars = true,
  onHover,
}: FunnelChartProps) {
  const { isEnabled: isDemoEnabled } = useDemoMode()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [dimensions, setDimensions] = useState({ width: 1000, height })
  const [tooltip, setTooltip] = useState<{
    visible: boolean
    x: number
    y: number
    stage: FunnelStage | null
    compareStage?: FunnelStage | null
  }>({
    visible: false,
    x: 0,
    y: 0,
    stage: null,
    compareStage: null,
  })
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [tooltipReady, setTooltipReady] = useState(false)
  const [hoveredPlottedRowIdx, setHoveredPlottedRowIdx] = useState<
    number | null
  >(null)

  // Force re-render when tooltip becomes visible to recalculate position with actual dimensions
  useEffect(() => {
    if (tooltip.visible && tooltipRef.current && !tooltipReady) {
      // Use requestAnimationFrame to ensure the tooltip has been rendered
      requestAnimationFrame(() => {
        setTooltipReady(true)
      })
    } else if (!tooltip.visible && tooltipReady) {
      setTooltipReady(false)
    }
  }, [tooltip.visible, tooltipReady])

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height,
        })
      }
    }

    updateDimensions()
    window.addEventListener("resize", updateDimensions)
    return () => window.removeEventListener("resize", updateDimensions)
  }, [height])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !stages.length) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    // Set canvas size
    const dpr = window.devicePixelRatio || 1
    canvas.width = dimensions.width * dpr
    canvas.height = dimensions.height * dpr
    canvas.style.width = `${dimensions.width}px`
    canvas.style.height = `${dimensions.height}px`
    ctx.scale(dpr, dpr)

    // Clear canvas
    ctx.clearRect(0, 0, dimensions.width, dimensions.height)

    // Chart settings
    const margin = {
      top: 80,
      right: 40,
      bottom: showAbandonmentRates ? 80 : 40,
      left: 60,
    }
    const chartWidth = dimensions.width - margin.left - margin.right
    const chartHeight = dimensions.height - margin.top - margin.bottom

    const numBars = stages.length
    const categoryWidth = chartWidth / numBars

    const hasPlottedRows = plottedRows.length > 0

    // When plotted rows are active, scale only to plotted row values (hide aggregate)
    const values = stages.map(s => s.value)
    const compareValues = compareStages?.map(s => s.value) || []
    const plottedValues = plottedRows.flatMap(r => r.stages.map(s => s.value))
    const plottedCompareValues = plottedRows.flatMap(
      r => r.compareStages?.map(s => s.value) || [],
    )
    const allValues = hasPlottedRows
      ? [...plottedValues, ...plottedCompareValues]
      : [...values, ...compareValues, ...plottedValues, ...plottedCompareValues]
    const maxValue = Math.max(...allValues)

    // Draw grid lines
    if (showGridLines) {
      ctx.strokeStyle = gridColor
      ctx.lineWidth = 1
      ctx.setLineDash([3, 3])

      const gridLines = 5
      for (let i = 0; i <= gridLines; i++) {
        const y = margin.top + (chartHeight / gridLines) * i
        ctx.beginPath()
        ctx.moveTo(margin.left, y)
        ctx.lineTo(margin.left + chartWidth, y)
        ctx.stroke()

        // Y-axis labels
        const value = maxValue - (maxValue / gridLines) * i
        ctx.fillStyle = textSecondaryColor
        ctx.font = "12px sans-serif"
        ctx.textAlign = "right"
        ctx.fillText(
          Math.round(value).toLocaleString(),
          margin.left - 10,
          y + 4,
        )
      }
      ctx.setLineDash([])
    }

    // Calculate bar positions
    const barPositions = values.map((value, i) => {
      const x = margin.left + categoryWidth * i + (categoryWidth - barWidth) / 2
      const barHeight = (value / maxValue) * chartHeight
      const y = margin.top + chartHeight - barHeight
      return { x, y, height: barHeight, value }
    })

    // Draw flow areas between bars (on hover) — only when aggregate is visible
    if (isHovered && !hasPlottedRows) {
      for (let i = 0; i < barPositions.length - 1; i++) {
        const bar1 = barPositions[i]
        const bar2 = barPositions[i + 1]

        const x1 = bar1.x + barWidth
        const x2 = bar2.x
        const y1 = bar1.y
        const y2 = bar2.y

        // Draw trapezoid
        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)
        ctx.lineTo(x2, margin.top + chartHeight)
        ctx.lineTo(x1, margin.top + chartHeight)
        ctx.closePath()
        ctx.fillStyle = `${flowColor}40` // with opacity
        ctx.fill()

        // Draw top line
        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)
        ctx.strokeStyle = flowColor
        ctx.lineWidth = 2
        ctx.stroke()
      }

      // Draw comparison flow lines (on hover)
      if (compareStages?.length && compareStages.length === stages.length) {
        const compareBarPositions = compareStages.map((compareStage, i) => {
          const x =
            margin.left + categoryWidth * i + (categoryWidth - barWidth) / 2
          const barHeight = (compareStage.value / maxValue) * chartHeight
          const y = margin.top + chartHeight - barHeight
          return { x, y, height: barHeight, value: compareStage.value }
        })

        for (let i = 0; i < compareBarPositions.length - 1; i++) {
          const bar1 = compareBarPositions[i]
          const bar2 = compareBarPositions[i + 1]

          const x1 = bar1.x + barWidth
          const x2 = bar2.x
          const y1 = bar1.y
          const y2 = bar2.y

          // Draw comparison flow line (dotted white)
          ctx.beginPath()
          ctx.moveTo(x1, y1)
          ctx.lineTo(x2, y2)
          ctx.strokeStyle = "#FFFFFF"
          ctx.lineWidth = 2
          ctx.setLineDash([5, 5])
          ctx.stroke()
          ctx.setLineDash([])
        }
      }
    }

    // Draw bars and labels
    barPositions.forEach((bar, i) => {
      const stage = stages[i]

      // Aggregate bar — only when no plotted rows
      if (!hasPlottedRows) {
        ctx.fillStyle = barColor
        ctx.fillRect(bar.x, bar.y, barWidth, bar.height)
      }

      // Step label at top (always shown)
      if (showStepLabels) {
        ctx.fillStyle = textSecondaryColor
        ctx.font = "11px sans-serif"
        ctx.textAlign = "center"
        ctx.fillText(`Step ${i + 1}`, bar.x + barWidth / 2, margin.top - 45)
      }

      // Stage label (always shown)
      ctx.fillStyle = textColor
      ctx.font = "bold 13px sans-serif"
      ctx.textAlign = "center"
      ctx.fillText(stage.label, bar.x + barWidth / 2, margin.top - 25)

      // Percentage — only for aggregate mode
      if (!hasPlottedRows && stage.percentage !== undefined) {
        ctx.fillStyle = textColor
        ctx.font = "bold 14px sans-serif"
        ctx.fillText(
          `${stage.percentage.toFixed(1)}%`,
          bar.x + barWidth / 2,
          margin.top - 8,
        )
      }

      // Value at top of bar — only for aggregate mode
      if (!hasPlottedRows && showValuesInBars && bar.height > 30) {
        ctx.fillStyle = "#FFFFFF"
        ctx.font = "bold 12px sans-serif"
        ctx.fillText(
          bar.value.toLocaleString(),
          bar.x + barWidth / 2,
          bar.y + 20,
        )
      }

      // Abandonment rate below bar — only for aggregate mode
      if (
        !hasPlottedRows &&
        showAbandonmentRates &&
        i > 0 &&
        stage.abandonmentCount !== undefined
      ) {
        ctx.fillStyle = textSecondaryColor
        ctx.font = "11px sans-serif"
        ctx.fillText(
          "Abandonment rate",
          bar.x + barWidth / 2,
          margin.top + chartHeight + 20,
        )

        ctx.fillStyle = textColor
        ctx.font = "bold 12px sans-serif"
        ctx.fillText(
          stage.abandonmentCount.toLocaleString(),
          bar.x + barWidth / 2,
          margin.top + chartHeight + 35,
        )

        if (stage.abandonmentRate !== undefined) {
          ctx.fillStyle = textSecondaryColor
          ctx.font = "11px sans-serif"
          ctx.fillText(
            `${stage.abandonmentRate.toFixed(1)}%`,
            bar.x + barWidth / 2,
            margin.top + chartHeight + 50,
          )
        }
      }
    })

    // Draw comparison dotted lines ON TOP — only when aggregate is visible
    if (
      !hasPlottedRows &&
      compareStages?.length &&
      compareStages.length === stages.length
    ) {
      const compareBarPositions = compareStages.map((compareStage, i) => {
        const x =
          margin.left + categoryWidth * i + (categoryWidth - barWidth) / 2
        const barHeight = (compareStage.value / maxValue) * chartHeight
        const y = margin.top + chartHeight - barHeight
        return { x, y, height: barHeight, value: compareStage.value }
      })

      // Draw dotted lines at top of comparison bars
      ctx.strokeStyle = "#FFFFFF" // White color for comparison
      ctx.lineWidth = 2
      ctx.setLineDash([5, 5]) // Dotted line pattern

      compareBarPositions.forEach(compareBar => {
        const x1 = compareBar.x
        const x2 = compareBar.x + barWidth
        const y = compareBar.y

        ctx.beginPath()
        ctx.moveTo(x1, y)
        ctx.lineTo(x2, y)
        ctx.stroke()

        // Draw comparison value with background directly on the line
        ctx.setLineDash([]) // Reset for text

        const textX = compareBar.x + barWidth / 2
        const textY = compareBar.y + 4 // Position text on the line
        const text = compareBar.value.toLocaleString()

        // Measure text to draw background
        ctx.font = "bold 11px sans-serif"
        const textMetrics = ctx.measureText(text)
        const textWidth = textMetrics.width
        const textHeight = 12
        const padding = 3

        // Draw semi-transparent background on the line
        ctx.fillStyle = "rgba(0, 0, 0, 0.7)"
        ctx.fillRect(
          textX - textWidth / 2 - padding,
          textY - textHeight + 1,
          textWidth + padding * 2,
          textHeight + padding,
        )

        // Draw white text on top
        ctx.fillStyle = "#FFFFFF"
        ctx.textAlign = "center"
        ctx.fillText(text, textX, textY)

        ctx.setLineDash([5, 5]) // Restore for next line
      })

      ctx.setLineDash([]) // Reset line dash
    }

    // Draw plotted rows — primary bars when aggregate is hidden, overlays otherwise
    if (plottedRows.length > 0) {
      plottedRows.forEach((row, rowIdx) => {
        if (row.stages.length !== stages.length) return

        const isHoveredRow = hoveredPlottedRowIdx === rowIdx
        // When primary (no aggregate), use solid fill; when overlay, use transparent
        let fillOpacity: string
        if (hasPlottedRows) {
          fillOpacity = isHoveredRow ? "CC" : "99" // ~80% vs ~60%
        } else {
          fillOpacity = isHoveredRow ? "80" : "30" // ~50% vs ~19%
        }

        row.stages.forEach((stage, i) => {
          const barHeight = (stage.value / maxValue) * chartHeight
          const x =
            margin.left + categoryWidth * i + (categoryWidth - barWidth) / 2
          const y = margin.top + chartHeight - barHeight

          // Draw filled bar
          ctx.fillStyle = row.color + fillOpacity
          ctx.fillRect(x, y, barWidth, barHeight)

          // Draw solid top line
          ctx.beginPath()
          ctx.moveTo(x, y)
          ctx.lineTo(x + barWidth, y)
          ctx.strokeStyle = row.color
          ctx.lineWidth = 2.5
          ctx.setLineDash([])
          ctx.stroke()

          // Show value label in bar when primary mode
          if (hasPlottedRows && showValuesInBars && barHeight > 30) {
            ctx.fillStyle = "#FFFFFF"
            ctx.font = "bold 12px sans-serif"
            ctx.textAlign = "center"
            ctx.fillText(stage.value.toLocaleString(), x + barWidth / 2, y + 20)
          }
        })

        // Draw comparison dotted lines for plotted rows
        if (row.compareStages && row.compareStages.length === stages.length) {
          row.compareStages.forEach((stage, i) => {
            const barHeight = (stage.value / maxValue) * chartHeight
            const x =
              margin.left + categoryWidth * i + (categoryWidth - barWidth) / 2
            const y = margin.top + chartHeight - barHeight

            ctx.beginPath()
            ctx.moveTo(x, y)
            ctx.lineTo(x + barWidth, y)
            ctx.strokeStyle = row.color
            ctx.lineWidth = 2
            ctx.setLineDash([5, 5])
            ctx.stroke()
            ctx.setLineDash([])
          })
        }
      })
    }
  }, [
    stages,
    compareStages,
    plottedRows,
    hoveredPlottedRowIdx,
    dimensions,
    isHovered,
    barWidth,
    barColor,
    flowColor,
    textColor,
    textSecondaryColor,
    gridColor,
    showStepLabels,
    showAbandonmentRates,
    showGridLines,
    showValuesInBars,
  ])

  const handleMouseEnter = () => {
    setIsHovered(true)
    if (onHover) onHover(null)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    setTooltip({ visible: false, x: 0, y: 0, stage: null, compareStage: null })
    if (onHover) onHover(null)
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    const margin = {
      top: 80,
      right: 40,
      bottom: showAbandonmentRates ? 80 : 40,
      left: 60,
    }
    const chartWidth = dimensions.width - margin.left - margin.right
    const chartHeight = dimensions.height - margin.top - margin.bottom
    const numBars = stages.length
    const categoryWidth = chartWidth / numBars

    const hasPlottedRows = plottedRows.length > 0
    const values = stages.map(s => s.value)
    const compareValues = compareStages?.map(s => s.value) || []
    const plottedValues = plottedRows.flatMap(r => r.stages.map(s => s.value))
    const plottedCompareValues = plottedRows.flatMap(
      r => r.compareStages?.map(s => s.value) || [],
    )
    const maxValue = hasPlottedRows
      ? Math.max(...plottedValues, ...plottedCompareValues)
      : Math.max(
          ...values,
          ...compareValues,
          ...plottedValues,
          ...plottedCompareValues,
        )

    // Check if hovering near a plotted row's top line
    let nearestRowIdx: number | null = null
    let nearestDist = 8
    for (let rowIdx = 0; rowIdx < plottedRows.length; rowIdx++) {
      const row = plottedRows[rowIdx]
      if (row.stages.length !== stages.length) continue
      for (let i = 0; i < row.stages.length; i++) {
        const barX =
          margin.left + categoryWidth * i + (categoryWidth - barWidth) / 2
        if (x >= barX && x <= barX + barWidth) {
          const barHeight = (row.stages[i].value / maxValue) * chartHeight
          const lineY = margin.top + chartHeight - barHeight
          const dist = Math.abs(y - lineY)
          if (dist < nearestDist) {
            nearestDist = dist
            nearestRowIdx = rowIdx
          }
        }
      }
    }
    setHoveredPlottedRowIdx(nearestRowIdx)

    // Check which bar is being hovered
    for (let i = 0; i < stages.length; i++) {
      const barX =
        margin.left + categoryWidth * i + (categoryWidth - barWidth) / 2
      const barEndX = barX + barWidth

      if (x >= barX && x <= barEndX) {
        setTooltip({
          visible: true,
          x: e.clientX,
          y: e.clientY,
          stage: stages[i],
          compareStage: compareStages?.[i] || null,
        })
        return
      }
    }

    // Not hovering over any bar
    setTooltip({ visible: false, x: 0, y: 0, stage: null, compareStage: null })
  }

  // Calculate dynamic tooltip position
  const getTooltipPosition = () => {
    if (!containerRef.current) {
      return { left: 0, top: 0 }
    }

    const containerRect = containerRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    const offset = 15

    // Get actual tooltip dimensions if available, otherwise use estimates
    let tooltipWidth = 300
    let tooltipHeight = 200

    if (tooltipRef.current && tooltipReady) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      tooltipWidth = tooltipRect.width
      tooltipHeight = tooltipRect.height
    }

    let left = tooltip.x + offset
    let top = tooltip.y - tooltipHeight / 2

    // Check right boundary - if tooltip goes off right edge, show it to the left of cursor
    if (left + tooltipWidth > viewportWidth - 20) {
      left = tooltip.x - tooltipWidth - offset
    }

    // Check left boundary
    if (left < 20) {
      left = 20
    }

    // Check bottom boundary
    if (top + tooltipHeight > viewportHeight - 20) {
      top = viewportHeight - tooltipHeight - 20
    }

    // Check top boundary
    if (top < 20) {
      top = 20
    }

    // Convert to relative positioning within container
    return {
      left: left - containerRect.left,
      top: top - containerRect.top,
    }
  }

  if (!stages.length) {
    return (
      <div className="flex items-center justify-center h-96 text-system-gray-400">
        No data available
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      className="relative rounded-lg cursor-pointer"
      style={{ backgroundColor: `${backgroundColor}50` }}
    >
      <canvas ref={canvasRef} className="w-full" style={{ display: "block" }} />

      {/* Hover hint */}
      {!isHovered && (
        <div className="absolute top-2 right-2 text-xs text-system-gray-500 bg-system-blue-900/50 px-3 py-1 rounded">
          Hover to see funnel flow
        </div>
      )}

      {/* Tooltip */}
      {tooltip.visible && tooltip.stage && (
        <div
          ref={tooltipRef}
          className="absolute pointer-events-none z-50"
          style={{
            ...getTooltipPosition(),
          }}
        >
          <Card rounded className="bg-basic-black border border-basic-blue p-3">
            {/* Header with Marathon Logo */}
            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-basic-blue">
              <MarathonDataLogo />
              <P className="text-white font-semibold text-base m-0">
                {tooltip.stage.label}
              </P>
            </div>

            {tooltip.compareStage ? (
              <table className="table table-zebra border-spacing-y-1 border-separate w-full">
                <thead>
                  <tr>
                    <th className="p-0.5">Metric</th>
                    <th className="p-0.5 text-center">Actual</th>
                    <th className="p-0.5 text-center">Compare</th>
                    <th className="p-0.5 text-end">Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Volume */}
                  <tr className="text-sm">
                    <td className="p-0.5 pe-4">Volume</td>
                    <td
                      className="py-0.5 px-2 text-start"
                      style={{
                        borderLeft: `3px solid ${getLightColor("#EB89B5")}`,
                      }}
                    >
                      {formatNumber(tooltip.stage.value, {
                        compact: true,
                        decimals: 0,
                      })}
                    </td>
                    <td
                      className="py-0.5 px-2 text-start"
                      style={{
                        borderLeft: `3px dashed ${getLightColor("#EB89B5")}`,
                      }}
                    >
                      {formatNumber(tooltip.compareStage.value, {
                        compact: true,
                        decimals: 0,
                      })}
                    </td>
                    <td
                      className={`p-0.5 px-2 text-end ${
                        tooltip.stage.value >= tooltip.compareStage.value
                          ? "text-green-bright"
                          : "text-error"
                      }`}
                    >
                      {tooltip.stage.value >= tooltip.compareStage.value
                        ? "+"
                        : ""}
                      {formatNumber(
                        calculatePercentageChange(
                          tooltip.stage.value,
                          tooltip.compareStage.value,
                        ),
                        { decimals: 1 },
                      )}
                      %
                    </td>
                  </tr>

                  {/* Conversion */}
                  {tooltip.stage.percentage !== undefined &&
                    tooltip.compareStage.percentage !== undefined && (
                      <tr className="text-sm">
                        <td className="p-0.5 pe-4">Conversion</td>
                        <td
                          className="py-0.5 px-2 text-start"
                          style={{
                            borderLeft: `3px solid ${getLightColor("#EB89B5")}`,
                          }}
                        >
                          {formatNumber(tooltip.stage.percentage, {
                            decimals: 1,
                          })}
                          %
                        </td>
                        <td
                          className="py-0.5 px-2 text-start"
                          style={{
                            borderLeft: `3px dashed ${getLightColor("#EB89B5")}`,
                          }}
                        >
                          {formatNumber(tooltip.compareStage.percentage, {
                            decimals: 1,
                          })}
                          %
                        </td>
                        <td
                          className={`p-0.5 px-2 text-end ${
                            tooltip.stage.percentage >=
                            tooltip.compareStage.percentage
                              ? "text-green-bright"
                              : "text-error"
                          }`}
                        >
                          {tooltip.stage.percentage >=
                          tooltip.compareStage.percentage
                            ? "+"
                            : ""}
                          {formatNumber(
                            tooltip.stage.percentage -
                              tooltip.compareStage.percentage,
                            { decimals: 1 },
                          )}
                          %
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            ) : (
              <div className="space-y-2">
                {/* Aggregate section — hidden when plotted rows are active */}
                {plottedRows.length === 0 && (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div className="text-sm w-auto flex flex-col">
                      <div className="text-white/50 mb-0.5">Volume</div>
                      <div className="text-sm font-bold text-left">
                        {formatNumber(tooltip.stage.value, {
                          compact: true,
                          decimals: 0,
                        })}
                      </div>
                    </div>
                    {tooltip.stage.percentage !== undefined && (
                      <div className="text-sm w-auto flex flex-col">
                        <div className="text-white/50 mb-0.5">Conversion</div>
                        <div className="text-sm font-bold text-left text-green-bright">
                          {formatNumber(tooltip.stage.percentage, {
                            decimals: 1,
                          })}
                          %
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Plotted row sections */}
                {plottedRows.map((row, rowIdx) => {
                  const stageIdx = stages.findIndex(
                    s => s.label === tooltip.stage?.label,
                  )
                  const rowStage = stageIdx >= 0 ? row.stages[stageIdx] : null
                  if (!rowStage) return null
                  const prevStage =
                    stageIdx > 0 ? row.stages[stageIdx - 1] : null
                  const stepConversion =
                    prevStage && prevStage.value > 0
                      ? (rowStage.value / prevStage.value) * 100
                      : null
                  return (
                    <div
                      key={row.id || `plotted-row-${rowIdx}`}
                      className={
                        rowIdx > 0 || plottedRows.length > 0
                          ? "border-t border-basic-blue pt-2"
                          : ""
                      }
                    >
                      <div
                        className={cn(
                          "text-xs font-semibold mb-1",
                          isDemoEnabled && "blur-md",
                        )}
                        style={{ color: row.color }}
                      >
                        {row.label}
                      </div>
                      <div className="grid grid-cols-2 gap-x-4">
                        <div className="text-sm w-auto flex flex-col">
                          <div className="text-white/50 mb-0.5">Volume</div>
                          <div className="text-sm font-bold text-left">
                            {formatNumber(rowStage.value, {
                              compact: true,
                              decimals: 0,
                            })}
                          </div>
                        </div>
                        {stepConversion !== null && (
                          <div className="text-sm w-auto flex flex-col">
                            <div className="text-white/50 mb-0.5">
                              Step Conv.
                            </div>
                            <div className="text-sm font-bold text-left text-green-bright">
                              {formatNumber(stepConversion, { decimals: 1 })}%
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  )
}
